import { useForm } from "react-hook-form";
import { useEffect, useRef } from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { useTranslation } from "react-i18next";
import { IconLoader2 } from "@tabler/icons-react";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import AppModal from "../AppModal";
import Loader from "../../Loader";
import { ModalActions, ModalContent, ModalHeader } from "../layout";
import { displayToast } from "../../app/AppToast";
import { apiClient, useApiQuery, type Api } from "../../../services/api";
import {
  reloadCurrentUser,
  useCurrentUser,
  type CurrentUser,
} from "../../../domains/user/hooks/useCurrentUser";
import { bigSearchInitialFromFirstName } from "../../../domains/user/utils/bigSearchInitialFromFirstName";
import { RegistryCoverage } from "../../RegistryCoverage";
import { NativeSelect } from "../../ui/NativeSelect";
import {
  HCP_REGISTRIES,
  registryForCountry,
  isWellFormedRegistryIdentifier,
  normalizeRegistryIdentifier,
  BIG_INITIALS_MAX_LENGTH,
  BIG_INITIALS_PATTERN,
} from "../../../../../api/src/shared/domain/hcp-registry";
import {
  HCP_PREFILL_BE_FLAG,
  HCP_PREFILL_NL_FLAG,
  HCP_PREFILL_PT_FLAG,
  HCP_PREFILL_DK_FLAG,
  HCP_PREFILL_US_FLAG,
  HCP_PREFILL_ES_FLAG,
} from "../../../../../api/src/shared/growthbook-flags";

type DirectoryLookupBody =
  Api["post"]["/webapp/public/hcp-directory/search"]["output"]["body"];
type DirectoryCandidate =
  | Extract<DirectoryLookupBody, { status: "found" }>["profile"]
  | Extract<DirectoryLookupBody, { status: "page" }>["candidates"][number];
type DirectorySearchResult =
  | Exclude<DirectoryLookupBody, { status: "page" }>
  | { status: "loading" }
  | {
      status: "page";
      candidates: DirectoryCandidate[];
      nextPage: number | null;
      loadState: "idle" | "loading" | "error";
    };

function repsUnmappedDescription(
  candidate: Extract<DirectoryCandidate, { registry: "reps" }>
): string {
  const labels = [
    ...new Set([
      ...(candidate.professionLabel === null
        ? candidate.qualificationLabels.length > 0
          ? candidate.qualificationLabels
          : candidate.categoryLabels
        : []),
      ...(candidate.specialtyLabel === null ? candidate.specialtyLabels : []),
    ]),
  ];
  return labels.length === 0 ? "" : ` · ${labels.join(", ")}`;
}

function useIdentifierForm({
  user,
  onSaved,
  npiPrefillEnabled,
  bePrefillEnabled,
  nlPrefillEnabled,
  ptPrefillEnabled,
  dkPrefillEnabled,
  esPrefillEnabled,
}: {
  user: CurrentUser;
  onSaved: () => void;
  npiPrefillEnabled: boolean;
  bePrefillEnabled: boolean;
  nlPrefillEnabled: boolean;
  ptPrefillEnabled: boolean;
  dkPrefillEnabled: boolean;
  esPrefillEnabled: boolean;
}) {
  const { t } = useTranslation();
  const capabilities = useApiQuery(
    "/webapp/public/hcp-directory",
    bePrefillEnabled ||
      nlPrefillEnabled ||
      ptPrefillEnabled ||
      dkPrefillEnabled ||
      esPrefillEnabled
      ? {}
      : false
  );
  const beDirectoryAdvertised =
    bePrefillEnabled &&
    capabilities.state === "success" &&
    capabilities.body.directories.some(
      (entry) => entry.registry === "health_pro"
    );
  const nlDirectoryAdvertised =
    nlPrefillEnabled &&
    capabilities.state === "success" &&
    capabilities.body.directories.some((entry) => entry.registry === "big");
  const stpsDirectory =
    capabilities.state === "success"
      ? capabilities.body.directories.find((entry) => entry.registry === "stps")
      : undefined;
  const stpsProfessions =
    stpsDirectory === undefined ? [] : stpsDirectory.professions;
  const dkDirectoryAdvertised = dkPrefillEnabled && stpsDirectory !== undefined;
  const ptDirectoryAdvertised =
    ptPrefillEnabled &&
    capabilities.state === "success" &&
    capabilities.body.directories.some((entry) => entry.registry === "om");
  const esDirectoryAdvertised =
    esPrefillEnabled &&
    capabilities.state === "success" &&
    capabilities.body.directories.some((entry) => entry.registry === "reps");
  const countryRegistry = registryForCountry(
    user.country === null ? "" : user.country
  );
  const ownInitial = bigSearchInitialFromFirstName(
    user.firstname === null ? "" : user.firstname
  );
  const form = useForm<{
    registry: keyof typeof HCP_REGISTRIES | "reps" | "";
    identifier: string;
    repsFirstName: string;
    searchText: string;
    initials: string;
    secondSurname: string;
    repsSubmittedQuery: {
      firstName: string;
      firstSurname: string;
      secondSurname: string;
    } | null;
    stpsProfessionCode: string;
    stpsSearchMode: "name" | "identifier";
    searchResult: DirectorySearchResult | null;
    selectedDirectoryCandidate: DirectoryCandidate | null;
  }>({
    defaultValues: {
      registry:
        user.country === "ES" && esPrefillEnabled
          ? "reps"
          : (countryRegistry === "npi" && !npiPrefillEnabled) ||
              (countryRegistry === "inami" && !bePrefillEnabled) ||
              (countryRegistry === "big" && !nlPrefillEnabled) ||
              (countryRegistry === "om" && !ptPrefillEnabled) ||
              (countryRegistry === "stps" && !dkPrefillEnabled)
            ? "rpps"
            : countryRegistry === null
              ? "rpps"
              : countryRegistry,
      identifier: "",
      repsFirstName: user.firstname === null ? "" : user.firstname,
      searchText: "",
      initials:
        countryRegistry === "big" && nlPrefillEnabled && ownInitial !== null
          ? ownInitial
          : "",
      secondSurname: "",
      repsSubmittedQuery: null,
      stpsProfessionCode: "",
      stpsSearchMode: "name",
      searchResult: null,
      selectedDirectoryCandidate: null,
    },
  });
  const registryField = form.register("registry");
  const initialsField = form.register("initials");
  const registry = form.watch("registry");
  const enteredIdentifier = form.watch("identifier");
  const identifier =
    registry === "stps"
      ? enteredIdentifier.trim()
      : enteredIdentifier.replace(/\s+/g, "");
  const repsFirstName = form.watch("repsFirstName");
  const searchText = form.watch("searchText").trim();
  const initials = form.watch("initials").trim();
  const stpsProfessionCode = form.watch("stpsProfessionCode");
  const stpsSearchMode = form.watch("stpsSearchMode");
  const stpsIdentifierInvalid =
    registry === "stps" &&
    stpsSearchMode === "identifier" &&
    !isWellFormedRegistryIdentifier(
      normalizeRegistryIdentifier({ registry, identifier: searchText })
    );
  const secondSurname = form.watch("secondSurname").trim();
  const repsSubmittedQuery = form.watch("repsSubmittedQuery");
  const searchResult = form.watch("searchResult");
  const searchPending =
    searchResult !== null && searchResult.status === "loading";
  const selectedDirectoryCandidate = form.watch("selectedDirectoryCandidate");
  const repsResultsCurrent =
    repsSubmittedQuery !== null &&
    repsSubmittedQuery.firstName === repsFirstName.trim() &&
    repsSubmittedQuery.firstSurname === searchText &&
    repsSubmittedQuery.secondSurname === secondSurname;
  const repsNameTooLong =
    repsFirstName.trim().length > 150 ||
    searchText.length > 150 ||
    secondSurname.length > 150;
  const directorySearchSequence = useRef(0);
  const directoryPageLoadingRef = useRef(false);
  const beDirectoryEnabled =
    beDirectoryAdvertised ||
    (bePrefillEnabled &&
      registry === "inami" &&
      capabilities.state === "error");
  const nlDirectoryEnabled =
    nlDirectoryAdvertised ||
    (nlPrefillEnabled && registry === "big" && capabilities.state === "error");
  const dkDirectoryEnabled =
    dkDirectoryAdvertised ||
    (dkPrefillEnabled && registry === "stps" && capabilities.state === "error");
  const ptDirectoryEnabled =
    ptDirectoryAdvertised ||
    (ptPrefillEnabled && registry === "om" && capabilities.state === "error");
  const esDirectoryEnabled =
    esDirectoryAdvertised ||
    (esPrefillEnabled && registry === "reps" && capabilities.state === "error");
  const extendedRegistryEnabled =
    npiPrefillEnabled ||
    beDirectoryEnabled ||
    nlDirectoryEnabled ||
    ptDirectoryEnabled ||
    dkDirectoryEnabled ||
    esDirectoryEnabled;
  const registryEnabled =
    registry === "rpps" ||
    (registry === "npi" && npiPrefillEnabled) ||
    (registry === "inami" &&
      bePrefillEnabled &&
      (capabilities.state === "pending" ||
        capabilities.state === "error" ||
        beDirectoryAdvertised)) ||
    (registry === "big" &&
      nlPrefillEnabled &&
      (capabilities.state === "pending" ||
        capabilities.state === "error" ||
        nlDirectoryAdvertised)) ||
    (registry === "stps" &&
      dkPrefillEnabled &&
      (capabilities.state === "pending" ||
        capabilities.state === "error" ||
        dkDirectoryAdvertised)) ||
    (registry === "om" &&
      ptPrefillEnabled &&
      (capabilities.state === "pending" ||
        capabilities.state === "error" ||
        ptDirectoryAdvertised)) ||
    (registry === "reps" &&
      esPrefillEnabled &&
      (capabilities.state === "pending" ||
        capabilities.state === "error" ||
        esDirectoryAdvertised));
  const directoryPending =
    (registry === "inami" ||
      registry === "big" ||
      registry === "om" ||
      registry === "stps" ||
      registry === "reps") &&
    capabilities.state === "pending";
  const directoryUnavailable =
    (registry === "inami" ||
      registry === "big" ||
      registry === "om" ||
      registry === "stps" ||
      registry === "reps") &&
    capabilities.state === "error";
  useEffect(() => {
    if (registryEnabled) return;
    directorySearchSequence.current += 1;
    form.reset({
      registry: "rpps",
      identifier: "",
      repsFirstName: user.firstname === null ? "" : user.firstname,
      searchText: "",
      initials: "",
      secondSurname: "",
      repsSubmittedQuery: null,
      stpsProfessionCode: "",
      stpsSearchMode: "name",
      searchResult: null,
      selectedDirectoryCandidate: null,
    });
  }, [registryEnabled, form]);
  const clearDirectorySelection = () => {
    directoryPageLoadingRef.current = false;
    form.setValue("searchResult", null);
    form.setValue("selectedDirectoryCandidate", null);
    if (selectedDirectoryCandidate !== null) form.setValue("identifier", "");
  };
  const editDirectorySearch = () => {
    directorySearchSequence.current += 1;
    if (registry !== "reps") {
      clearDirectorySelection();
      return;
    }
    directoryPageLoadingRef.current = false;
    form.setValue("selectedDirectoryCandidate", null);
    if (
      searchResult !== null &&
      searchResult.status === "page" &&
      searchResult.loadState === "loading"
    )
      form.setValue("searchResult", { ...searchResult, loadState: "idle" });
    if (
      searchResult !== null &&
      searchResult.status !== "page" &&
      searchResult.status !== "multiple" &&
      searchResult.status !== "found"
    )
      form.setValue("searchResult", null);
  };
  const canonicalIdentifier =
    registry === "" || registry === "reps"
      ? identifier
      : normalizeRegistryIdentifier({ registry, identifier }).identifier;
  const hasCurrentDirectorySelection =
    selectedDirectoryCandidate !== null &&
    ((registry === "inami" &&
      selectedDirectoryCandidate.registry === "health_pro") ||
      (registry === "big" && selectedDirectoryCandidate.registry === "big") ||
      (registry === "om" && selectedDirectoryCandidate.registry === "om") ||
      (registry === "stps" && selectedDirectoryCandidate.registry === "stps") ||
      (registry === "reps" && selectedDirectoryCandidate.registry === "reps"));
  const valid =
    registryEnabled &&
    !directoryPending &&
    (!directoryUnavailable ||
      registry === "om" ||
      registry === "stps" ||
      hasCurrentDirectorySelection) &&
    ((registry === "reps" &&
      selectedDirectoryCandidate !== null &&
      selectedDirectoryCandidate.registry === "reps") ||
      (registry !== "reps" &&
        isWellFormedRegistryIdentifier({
          registry,
          identifier: canonicalIdentifier,
        })) ||
      (registry === "inami" &&
        selectedDirectoryCandidate !== null &&
        selectedDirectoryCandidate.identifier === null));
  const searchDirectory = async () => {
    const currentSearch = form.getValues("searchResult");
    if (currentSearch !== null && currentSearch.status === "loading") return;
    if (!registryEnabled || capabilities.state !== "success") return;
    if (stpsIdentifierInvalid) return;
    if (
      registry !== "inami" &&
      registry !== "big" &&
      registry !== "om" &&
      registry !== "stps" &&
      registry !== "reps"
    )
      return;
    const sequence = ++directorySearchSequence.current;
    clearDirectorySelection();
    const numeric =
      registry === "reps"
        ? false
        : registry === "stps"
          ? stpsSearchMode === "identifier"
          : registry === "big" || registry === "om"
            ? isWellFormedRegistryIdentifier(
                normalizeRegistryIdentifier({
                  registry,
                  identifier: searchText,
                })
              )
            : /^(?:[0-9]{8}|[0-9]{11})$/.test(searchText);
    if (!numeric && /^[0-9]+$/.test(searchText)) return;
    if (
      !numeric &&
      searchText.length < (registry === "big" || registry === "reps" ? 2 : 3)
    )
      return;
    if (
      registry === "big" &&
      !numeric &&
      initials !== "" &&
      !BIG_INITIALS_PATTERN.test(initials)
    )
      return;
    if (!numeric && registry === "om" && searchText.split(/\s+/).length < 2)
      return;
    if (registry === "reps" && repsNameTooLong) return;
    if (registry === "reps")
      form.setValue("repsSubmittedQuery", {
        firstName: repsFirstName.trim(),
        firstSurname: searchText,
        secondSurname,
      });
    form.setValue("searchResult", { status: "loading" });
    try {
      let { body } = await apiClient.post(
        "/webapp/public/hcp-directory/search",
        {
          json:
            registry === "reps"
              ? {
                  registry: "reps",
                  mode: "name",
                  firstName: repsFirstName.trim(),
                  firstSurname: searchText,
                  secondSurname: secondSurname === "" ? null : secondSurname,
                  page: 1,
                }
              : numeric
                ? {
                    registry: registry === "inami" ? "health_pro" : registry,
                    mode: "identifier",
                    identifier:
                      registry === "stps"
                        ? normalizeRegistryIdentifier({
                            registry,
                            identifier: searchText,
                          }).identifier
                        : searchText,
                  }
                : registry === "stps"
                  ? {
                      registry: "stps",
                      mode: "name",
                      name: searchText,
                      professionCode:
                        stpsProfessionCode === "" ? null : stpsProfessionCode,
                    }
                  : registry === "big"
                    ? {
                        registry: "big",
                        mode: "name",
                        name: searchText,
                        initials: initials === "" ? null : initials,
                      }
                    : {
                        registry: registry === "inami" ? "health_pro" : "om",
                        mode: "name",
                        name: searchText,
                        page: 1,
                      },
        }
      );
      if (sequence !== directorySearchSequence.current) return;
      if (
        registry === "big" &&
        !numeric &&
        ownInitial !== null &&
        initials === ownInitial &&
        !form.getFieldState("initials").isTouched &&
        body.status === "not_found"
      ) {
        ({ body } = await apiClient.post(
          "/webapp/public/hcp-directory/search",
          {
            json: {
              registry: "big",
              mode: "name",
              name: searchText,
              initials: null,
            },
          }
        ));
        if (sequence !== directorySearchSequence.current) return;
      }
      form.setValue(
        "searchResult",
        body.status === "page"
          ? {
              status: "page",
              candidates: body.candidates,
              nextPage: body.nextPage,
              loadState: "idle",
            }
          : body
      );
    } catch {
      if (sequence !== directorySearchSequence.current) return;
      form.setValue("searchResult", null);
      displayToast(
        registry === "stps"
          ? t("The registry is unavailable. You can continue manually.")
          : registry === "reps"
            ? t("REPS is unavailable. Try again later.")
            : registry === "big"
              ? t(
                  "BIG-register is unavailable. You can enter your BIG number manually."
                )
              : registry === "om"
                ? t(
                    "Ordem dos Médicos is unavailable. You can enter your cédula number manually."
                  )
                : t(
                    "Health Pro is unavailable. You can enter your INAMI number manually."
                  ),
        "warning"
      );
    }
  };
  const loadMoreDirectory = async () => {
    if (registry === "reps" && !repsResultsCurrent) return;
    if (
      (registry !== "inami" && registry !== "om" && registry !== "reps") ||
      searchResult === null ||
      searchResult.status !== "page" ||
      searchResult.nextPage === null ||
      directoryPageLoadingRef.current
    )
      return;
    const page = searchResult.nextPage;
    const sequence = directorySearchSequence.current;
    directoryPageLoadingRef.current = true;
    form.setValue("searchResult", { ...searchResult, loadState: "loading" });
    try {
      const { body } = await apiClient.post(
        "/webapp/public/hcp-directory/search",
        {
          json:
            registry === "reps"
              ? {
                  registry: "reps",
                  mode: "name",
                  firstName: repsFirstName.trim(),
                  firstSurname: searchText,
                  secondSurname: secondSurname === "" ? null : secondSurname,
                  page,
                }
              : {
                  registry: registry === "inami" ? "health_pro" : "om",
                  mode: "name",
                  name: searchText,
                  page,
                },
        }
      );
      if (sequence !== directorySearchSequence.current) return;
      if (body.status !== "page")
        throw new Error("Directory page search returned no page");
      const current = form.getValues("searchResult");
      if (
        current === null ||
        current.status !== "page" ||
        current.nextPage !== page
      )
        return;
      const seen = new Set(
        current.candidates.map((candidate) => candidate.sourceRecordId)
      );
      form.setValue("searchResult", {
        status: "page",
        candidates: [
          ...current.candidates,
          ...body.candidates.filter((candidate) => {
            if (seen.has(candidate.sourceRecordId)) return false;
            seen.add(candidate.sourceRecordId);
            return true;
          }),
        ],
        nextPage: body.nextPage,
        loadState: "idle",
      });
    } catch {
      if (sequence !== directorySearchSequence.current) return;
      const current = form.getValues("searchResult");
      if (
        current !== null &&
        current.status === "page" &&
        current.nextPage === page
      )
        form.setValue("searchResult", { ...current, loadState: "error" });
    } finally {
      if (sequence === directorySearchSequence.current)
        directoryPageLoadingRef.current = false;
    }
  };
  const submit = form.handleSubmit(async () => {
    if (registry === "" || !valid) return;
    try {
      await apiClient.patch("/webapp/private/users/me", {
        json: {
          ...((registry === "inami" ||
            registry === "big" ||
            registry === "om" ||
            registry === "stps" ||
            registry === "reps") &&
          selectedDirectoryCandidate !== null
            ? {
                healthProReceipt: selectedDirectoryCandidate.selectionReceipt,
              }
            : {}),
          ...((registry === "inami" ||
            registry === "big" ||
            registry === "om" ||
            registry === "stps" ||
            registry === "reps") &&
          selectedDirectoryCandidate !== null
            ? {}
            : registry === "reps"
              ? {}
              : {
                  hcpRegistryIds: [
                    ...user.hcpRegistryIds
                      .filter((entry) => entry.registry !== registry)
                      .map((entry) => ({
                        registry: entry.registry,
                        identifier: entry.identifier,
                      })),
                    { registry, identifier: canonicalIdentifier },
                  ],
                }),
        },
      });
      const refreshedUser = await reloadCurrentUser();
      if (refreshedUser === null) return;
      if (refreshedUser.professionalRecordStatus === "missing") {
        displayToast(t("Something went wrong. Please try again."), "error");
        return;
      }
      onSaved();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  });
  return {
    t,
    extendedRegistryEnabled,
    directoryPending,
    directoryUnavailable,
    retryDirectoryCapabilities: capabilities.refetch,
    beDirectoryEnabled,
    nlDirectoryEnabled,
    ptDirectoryEnabled,
    dkDirectoryEnabled,
    stpsProfessions,
    stpsProfessionCode,
    stpsSearchMode,
    stpsIdentifierInvalid,
    esDirectoryEnabled,
    form,
    registryField,
    initialsField,
    registry,
    identifier,
    repsFirstName,
    searchText,
    initials,
    secondSurname,
    repsResultsCurrent,
    repsNameTooLong,
    searchPending,
    ownInitial,
    searchResult,
    selectedDirectoryCandidate,
    directorySearchSequence,
    clearDirectorySelection,
    editDirectorySearch,
    valid,
    searchDirectory,
    loadMoreDirectory,
    submit,
  };
}

function IdentifierForm({
  user,
  onSaved,
  onCancel,
  npiPrefillEnabled,
  bePrefillEnabled,
  nlPrefillEnabled,
  ptPrefillEnabled,
  dkPrefillEnabled,
  esPrefillEnabled,
}: {
  user: CurrentUser;
  onSaved: () => void;
  onCancel: () => void;
  npiPrefillEnabled: boolean;
  bePrefillEnabled: boolean;
  nlPrefillEnabled: boolean;
  ptPrefillEnabled: boolean;
  dkPrefillEnabled: boolean;
  esPrefillEnabled: boolean;
}) {
  const {
    t,
    extendedRegistryEnabled,
    directoryPending,
    directoryUnavailable,
    retryDirectoryCapabilities,
    beDirectoryEnabled,
    nlDirectoryEnabled,
    ptDirectoryEnabled,
    dkDirectoryEnabled,
    stpsProfessions,
    stpsProfessionCode,
    stpsSearchMode,
    stpsIdentifierInvalid,
    esDirectoryEnabled,
    form,
    registryField,
    initialsField,
    registry,
    identifier,
    repsFirstName,
    searchText,
    initials,
    secondSurname,
    repsResultsCurrent,
    repsNameTooLong,
    searchPending,
    ownInitial,
    searchResult,
    selectedDirectoryCandidate,
    directorySearchSequence,
    clearDirectorySelection,
    editDirectorySearch,
    valid,
    searchDirectory,
    loadMoreDirectory,
    submit,
  } = useIdentifierForm({
    user,
    onSaved,
    npiPrefillEnabled,
    bePrefillEnabled,
    nlPrefillEnabled,
    ptPrefillEnabled,
    dkPrefillEnabled,
    esPrefillEnabled,
  });
  const bigNumberSearch =
    registry === "big" &&
    isWellFormedRegistryIdentifier(
      normalizeRegistryIdentifier({ registry: "big", identifier: searchText })
    );
  if (directoryPending) return <Loader />;
  return (
    <form onSubmit={submit}>
      <ModalHeader>
        <span className="heading">{t("Add a professional record")}</span>
      </ModalHeader>
      <ModalContent
        className={
          extendedRegistryEnabled
            ? "flex flex-col gap-4"
            : "scrollbar-hidden [&_.MuiInputBase-input]:bg-[#f5f5f5]! flex flex-col gap-4"
        }
      >
        <p style={extendedRegistryEnabled ? {} : { marginTop: 0 }}>
          {registry === "reps"
            ? t("Select your REPS record for this room.")
            : extendedRegistryEnabled
              ? t(
                  "Enter a professional registry number or select your directory record for this room."
                )
              : t("Enter your professional registry number for this room.")}
        </p>
        {registry !== "" && <RegistryCoverage registry={registry} />}
        {registry === "reps" && (
          <p>
            {t(
              "REPS has incomplete coverage. You can select a listed record without a public number."
            )}
          </p>
        )}
        {directoryUnavailable && (
          <div className="flex flex-col gap-3">
            <p>{t("Something went wrong. Please try again.")}</p>
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => {
                void retryDirectoryCapabilities();
              }}
            >
              {t("Try again")}
            </Button>
          </div>
        )}
        {extendedRegistryEnabled && (
          <label>
            {t("Registry")}
            <NativeSelect
              {...registryField}
              onChange={(event) => {
                void registryField.onChange(event);
                directorySearchSequence.current += 1;
                form.setValue("identifier", "");
                form.setValue(
                  "repsFirstName",
                  user.firstname === null ? "" : user.firstname
                );
                form.setValue("searchText", "");
                form.setValue("repsSubmittedQuery", null);
                form.setValue("secondSurname", "");
                form.setValue("stpsProfessionCode", "");
                form.setValue("stpsSearchMode", "name");
                form.resetField("initials", {
                  defaultValue:
                    event.target.value === "big" && ownInitial !== null
                      ? ownInitial
                      : "",
                });
                clearDirectorySelection();
              }}
            >
              <option value="rpps">RPPS</option>
              {npiPrefillEnabled && <option value="npi">NPI</option>}
              {beDirectoryEnabled && <option value="inami">INAMI</option>}
              {nlDirectoryEnabled && <option value="big">BIG</option>}
              {ptDirectoryEnabled && (
                <option value="om">Ordem dos Médicos</option>
              )}
              {dkDirectoryEnabled && (
                <option value="stps">Autorisations-ID</option>
              )}
              {esDirectoryEnabled && <option value="reps">REPS</option>}
            </NativeSelect>
          </label>
        )}
        {((registry === "inami" && beDirectoryEnabled) ||
          (registry === "big" && nlDirectoryEnabled) ||
          (registry === "om" && ptDirectoryEnabled) ||
          (registry === "stps" && dkDirectoryEnabled) ||
          (registry === "reps" && esDirectoryEnabled)) && (
          <div className="flex flex-col gap-3">
            {registry === "reps" && (
              <label className="flex flex-col gap-2">
                {t("First name in REPS")}
                <Input
                  type="text"
                  value={repsFirstName}
                  maxLength={150}
                  onChange={(event) => {
                    editDirectorySearch();
                    form.setValue("repsFirstName", event.target.value);
                  }}
                />
              </label>
            )}
            {registry === "stps" && (
              <fieldset className="flex flex-col gap-2">
                <legend>{t("Search by name or Autorisations-ID")}</legend>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="stps-search-mode"
                    checked={stpsSearchMode === "name"}
                    onChange={() => {
                      editDirectorySearch();
                      form.setValue("stpsSearchMode", "name");
                    }}
                  />
                  {t("Name in the Danish register")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="stps-search-mode"
                    checked={stpsSearchMode === "identifier"}
                    onChange={() => {
                      editDirectorySearch();
                      form.setValue("stpsSearchMode", "identifier");
                    }}
                  />
                  Autorisations-ID
                </label>
              </fieldset>
            )}
            <Input
              type="text"
              value={searchText}
              maxLength={registry === "reps" ? 150 : undefined}
              placeholder={
                registry === "stps"
                  ? stpsSearchMode === "name"
                    ? t("Name in the Danish register")
                    : "Autorisations-ID"
                  : registry === "big"
                    ? t("Search BIG-register by surname or BIG number")
                    : registry === "reps"
                      ? t("First surname in REPS")
                      : registry === "om"
                        ? t(
                            "Search Ordem dos Médicos by full name or cédula number"
                          )
                        : t("Search Health Pro by name or INAMI number")
              }
              onChange={(event) => {
                editDirectorySearch();
                form.setValue("searchText", event.target.value);
              }}
            />
            {registry === "reps" && (
              <>
                <Input
                  type="text"
                  value={secondSurname}
                  maxLength={150}
                  placeholder={t("Second surname in REPS (optional)")}
                  onChange={(event) => {
                    editDirectorySearch();
                    form.setValue("secondSurname", event.target.value);
                  }}
                />
                <p>
                  {t(
                    "If your REPS record is missing, check the accents in your name."
                  )}
                </p>
              </>
            )}
            {registry === "stps" && stpsSearchMode === "name" && (
              <label>
                {t("Profession")}
                <NativeSelect
                  value={stpsProfessionCode}
                  onChange={(event) => {
                    directorySearchSequence.current += 1;
                    clearDirectorySelection();
                    form.setValue("stpsProfessionCode", event.target.value);
                  }}
                >
                  <option value="">{t("All professions")}</option>
                  {stpsProfessions.map((profession) => (
                    <option key={profession.code} value={profession.code}>
                      {profession.label}
                    </option>
                  ))}
                </NativeSelect>
              </label>
            )}
            {registry === "big" && (
              <label className="flex flex-col gap-2">
                {t("Initials registered with BIG (optional)")}
                <Input
                  type="text"
                  name={initialsField.name}
                  ref={initialsField.ref}
                  autoComplete="off"
                  maxLength={BIG_INITIALS_MAX_LENGTH}
                  value={initials}
                  placeholder={t("For example, D.A.M.P.J.")}
                  onChange={(event) => {
                    directorySearchSequence.current += 1;
                    clearDirectorySelection();
                    form.setValue("initials", event.target.value, {
                      shouldTouch: true,
                    });
                  }}
                />
              </label>
            )}
            {registry === "reps" && repsNameTooLong && (
              <p role="alert">
                {t("Use at most 150 characters in each REPS name field.")}
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              size="lg"
              leftIcon={
                registry === "reps" && searchPending ? (
                  <IconLoader2 className="animate-spin" />
                ) : null
              }
              disabled={
                directoryUnavailable ||
                searchPending ||
                stpsIdentifierInvalid ||
                (registry === "reps" &&
                  (searchText.length < 2 || repsNameTooLong)) ||
                (registry === "big" &&
                  !bigNumberSearch &&
                  initials !== "" &&
                  !BIG_INITIALS_PATTERN.test(initials))
              }
              onClick={() => {
                void searchDirectory();
              }}
            >
              {registry === "stps"
                ? t("Search the Danish register")
                : registry === "big"
                  ? t("Search BIG-register")
                  : registry === "reps"
                    ? searchPending
                      ? t("Searching REPS...")
                      : t("Search REPS")
                    : registry === "om"
                      ? t("Search Ordem dos Médicos")
                      : t("Search Health Pro")}
            </Button>
            {registry === "reps" &&
              searchResult !== null &&
              !repsResultsCurrent && (
                <p role="status">
                  {t("Search REPS again to update these results.")}
                </p>
              )}
            {searchResult !== null &&
              (searchResult.status === "found" ||
                searchResult.status === "multiple" ||
                searchResult.status === "page") &&
              (searchResult.status === "found"
                ? [searchResult.profile]
                : searchResult.candidates
              ).map((candidate) => (
                <button
                  type="button"
                  disabled={registry === "reps" && !repsResultsCurrent}
                  key={candidate.sourceRecordId}
                  className="rounded-xl border border-ds-slate-300 p-3 text-left disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => {
                    form.setValue("selectedDirectoryCandidate", candidate);
                    form.setValue(
                      "identifier",
                      candidate.identifier === null ? "" : candidate.identifier
                    );
                  }}
                >
                  {`${candidate.registry === "big" ? (candidate.initials === null ? "" : candidate.initials) : candidate.firstName === null ? "" : candidate.firstName} ${candidate.lastName}`.trim()}
                  {candidate.identifier === null
                    ? ""
                    : ` · ${candidate.registry === "stps" ? "Autorisations-ID" : candidate.registry === "big" ? "BIG" : candidate.registry === "om" ? "Cédula" : "INAMI"} ${candidate.identifier}`}
                  {candidate.registry === "stps"
                    ? ` · ${[
                        ...new Set(
                          [
                            candidate.professionLabel,
                            candidate.specialtyLabel,
                            candidate.sourceProfessionLabel,
                            ...candidate.qualificationLabels,
                          ].filter((label) => label !== null && label !== "")
                        ),
                      ].join(" · ")}`
                    : candidate.professionLabel === null
                      ? candidate.registry === "big"
                        ? ` · ${candidate.professionCode}`
                        : ""
                      : ` · ${candidate.professionLabel}`}
                  {candidate.registry === "stps"
                    ? ""
                    : candidate.registry === "om"
                      ? candidate.specialtyLabel === null
                        ? ""
                        : ` · ${candidate.specialtyLabel}`
                      : candidate.specialtyLabel === null
                        ? candidate.registry === "reps" ||
                          candidate.specialtyCodes.length === 0
                          ? ""
                          : ` · ${candidate.specialtyCodes.join(", ")}`
                        : ` · ${candidate.specialtyLabel}`}
                  {candidate.registry === "reps"
                    ? repsUnmappedDescription(candidate)
                    : ""}
                  {candidate.registry === "reps" &&
                  candidate.locations.length > 0
                    ? ` · ${candidate.locations.join(", ")}`
                    : ""}
                  {candidate.registry === "om" &&
                  candidate.qualificationLabels.length > 0
                    ? ` · ${candidate.qualificationLabels.join(", ")}`
                    : ""}
                  {candidate.registry === "om" &&
                  candidate.medicalDistrict !== ""
                    ? ` · ${candidate.medicalDistrict}`
                    : ""}
                  {candidate.registry === "reps" ||
                  candidate.registry === "big" ||
                  candidate.registry === "om" ||
                  candidate.registry === "stps" ||
                  candidate.addresses[0] === undefined
                    ? ""
                    : ` · ${candidate.addresses[0].city.fr === null ? candidate.addresses[0].city.nl : candidate.addresses[0].city.fr}`}
                  {selectedDirectoryCandidate !== null &&
                  selectedDirectoryCandidate.sourceRecordId ===
                    candidate.sourceRecordId
                    ? ` · ${t("Selected")}`
                    : ""}
                </button>
              ))}
            {searchResult !== null &&
              searchResult.status === "page" &&
              searchResult.nextPage !== null && (
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  disabled={
                    searchResult.loadState === "loading" ||
                    (registry === "reps" && !repsResultsCurrent)
                  }
                  onClick={() => {
                    void loadMoreDirectory();
                  }}
                >
                  {searchResult.loadState === "loading"
                    ? t("Loading more profiles...")
                    : searchResult.loadState === "error"
                      ? t("Retry loading profiles")
                      : t("Load more profiles")}
                </Button>
              )}
            {searchResult !== null &&
              searchResult.status === "page" &&
              searchResult.loadState === "error" && (
                <p role="alert">{t("Could not load more profiles.")}</p>
              )}
            {searchResult !== null &&
              searchResult.status === "too_many_results" && (
                <p>
                  {registry === "stps"
                    ? t(
                        "Refine your name or profession, or use your Autorisations-ID."
                      )
                    : registry === "big"
                      ? t(
                          "BIG returned no list. Add your registered initials and search again, or enter your BIG number."
                        )
                      : registry === "om"
                        ? t(
                            "More profiles match. Search with a fuller name or enter your cédula number."
                          )
                        : t(
                            "More profiles match. Search with a full name or INAMI number."
                          )}
                </p>
              )}
            {searchResult !== null && searchResult.status === "inactive" && (
              <p role="alert">{t("This authorization is inactive")}</p>
            )}
            {searchResult !== null &&
              (searchResult.status === "not_found" ||
                (searchResult.status === "page" &&
                  searchResult.candidates.length === 0 &&
                  searchResult.nextPage === null)) && (
                <p>
                  {registry === "stps"
                    ? t("No record found. You can continue manually.")
                    : registry === "big"
                      ? t(
                          "No BIG record found. You can enter your BIG number manually."
                        )
                      : registry === "reps"
                        ? t(
                            "No REPS record found. Check your surnames and try again."
                          )
                        : registry === "om"
                          ? t(
                              "No Ordem dos Médicos record found. You can enter your cédula number manually."
                            )
                          : t(
                              "No Health Pro record found. You can enter your INAMI number manually."
                            )}
                </p>
              )}
            {searchResult !== null &&
              (searchResult.status === "unavailable" ||
                searchResult.status === "disabled") && (
                <p>
                  {registry === "stps"
                    ? t(
                        "The registry is unavailable. You can continue manually."
                      )
                    : registry === "big"
                      ? t(
                          "BIG-register is unavailable. You can enter your BIG number manually."
                        )
                      : registry === "reps"
                        ? t("REPS is unavailable. Try again later.")
                        : registry === "om"
                          ? t(
                              "Ordem dos Médicos is unavailable. You can enter your cédula number manually."
                            )
                          : t(
                              "Health Pro is unavailable. You can enter your INAMI number manually."
                            )}
                </p>
              )}
          </div>
        )}
        {registry !== "reps" && (
          <Input
            type={extendedRegistryEnabled ? "text" : "number"}
            inputMode={registry === "stps" ? "text" : "numeric"}
            autoComplete="off"
            value={identifier}
            placeholder={t("Enter medical identifier")}
            aria-label={
              registry === ""
                ? t("Registry ID")
                : HCP_REGISTRIES[registry].label
            }
            onChange={(event) => {
              form.setValue(
                "identifier",
                extendedRegistryEnabled
                  ? event.target.value
                  : event.target.value.slice(0, 11),
                { shouldValidate: true }
              );
              if (
                (registry === "inami" ||
                  registry === "big" ||
                  registry === "om" ||
                  registry === "stps") &&
                selectedDirectoryCandidate !== null &&
                event.target.value.replace(/\s+/g, "") !==
                  selectedDirectoryCandidate.identifier
              )
                form.setValue("selectedDirectoryCandidate", null);
            }}
          />
        )}
      </ModalContent>
      <ModalActions
        className={
          extendedRegistryEnabled
            ? "flex-col!"
            : "flex-col! [&_button]:w-auto! [&_button]:self-stretch"
        }
      >
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className={extendedRegistryEnabled ? undefined : "w-full"}
          disabled={!valid || form.formState.isSubmitting}
        >
          {t("Save")}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="lg"
          className={extendedRegistryEnabled ? undefined : "w-full"}
          data-testid={extendedRegistryEnabled ? undefined : "dismiss-modal"}
          onClick={onCancel}
        >
          {t("Cancel")}
        </Button>
      </ModalActions>
    </form>
  );
}

export default function HcpIdentifierModal({
  visible,
  onSaved,
  onCancel,
}: {
  visible: boolean;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { currentUser } = useCurrentUser();
  const npiPrefillEnabled = useFeatureIsOn(HCP_PREFILL_US_FLAG);
  const bePrefillEnabled = useFeatureIsOn(HCP_PREFILL_BE_FLAG);
  const nlPrefillEnabled = useFeatureIsOn(HCP_PREFILL_NL_FLAG);
  const dkPrefillEnabled = useFeatureIsOn(HCP_PREFILL_DK_FLAG);
  const ptPrefillEnabled = useFeatureIsOn(HCP_PREFILL_PT_FLAG);
  const esPrefillEnabled = useFeatureIsOn(HCP_PREFILL_ES_FLAG);
  if (currentUser === null) return null;
  return (
    <AppModal modalStyle={{ backgroundColor: "white" }} visible={visible}>
      <IdentifierForm
        key={currentUser.country}
        user={currentUser}
        onSaved={onSaved}
        onCancel={onCancel}
        npiPrefillEnabled={npiPrefillEnabled}
        bePrefillEnabled={bePrefillEnabled}
        nlPrefillEnabled={nlPrefillEnabled}
        ptPrefillEnabled={ptPrefillEnabled}
        dkPrefillEnabled={dkPrefillEnabled}
        esPrefillEnabled={esPrefillEnabled}
      />
    </AppModal>
  );
}
